import { useCallback, useEffect, useMemo, useState } from "react";

import { analyticsCookieConsentApi } from "@/lib/api";
import {
  type AnalyticsCookieConsentMetadata,
  getAnalyticsCookieConsentMetadataFromPublicMetadata,
} from "@shared/constants";

import { useUser } from "@clerk/react";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";

import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  clearAnalyticsConsentCookie,
  readAnalyticsConsentCookie,
  resolveAnalyticsConsent,
  writeAnalyticsConsentCookie,
} from "./analyticsConsentCookie";
import { isPosthogBrowserAnalyticsEnabled } from "./posthogBrowserEnabled";

export type AnalyticsCookieConsentUiState = "unset" | "granted" | "denied";

export function useAnalyticsCookieConsent() {
  const posthog = usePostHog();
  const { user, isLoaded } = useUser();
  const [isUpdating, setIsUpdating] = useState(false);
  const [cookieConsent, setCookieConsent] = useState<AnalyticsCookieConsentMetadata | null>(
    readAnalyticsConsentCookie
  );

  const consent = useMemo((): AnalyticsCookieConsentUiState => {
    const meta = user?.publicMetadata as Record<string, unknown> | undefined;
    const accountConsent = getAnalyticsCookieConsentMetadataFromPublicMetadata(meta);
    return resolveAnalyticsConsent(cookieConsent, accountConsent)?.choice ?? "unset";
  }, [cookieConsent, user?.publicMetadata]);

  useEffect(() => {
    const refreshCookieConsent = () => setCookieConsent(readAnalyticsConsentCookie());
    window.addEventListener("focus", refreshCookieConsent);
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, refreshCookieConsent);
    return () => {
      window.removeEventListener("focus", refreshCookieConsent);
      window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, refreshCookieConsent);
    };
  }, []);

  const persistAndReload = useCallback(
    async (action: "grant" | "deny" | "revoke") => {
      setIsUpdating(true);
      try {
        if (action === "revoke") {
          if (user) {
            await analyticsCookieConsentApi.updateConsent(action);
            await user.reload();
          }
          clearAnalyticsConsentCookie();
          setCookieConsent(null);
          if (posthog) {
            posthog.reset();
            posthog.opt_out_capturing();
          }
          toast.success("Analytics consent withdrawn. You can choose again from the banner.");
          return;
        }

        const choice = action === "grant" ? "granted" : "denied";
        const nextConsent = writeAnalyticsConsentCookie(choice);
        setCookieConsent(nextConsent);

        if (choice === "granted") {
          posthog?.opt_in_capturing();
        } else {
          posthog?.opt_out_capturing();
        }

        if (user) {
          await analyticsCookieConsentApi.updateConsent(action);
          await user.reload();
        }

        if (action === "grant") {
          toast.success("Analytics cookies enabled.");
        } else {
          toast.success("Analytics cookies declined.");
        }
      } catch (e) {
        console.error("analytics cookie consent update failed", e);
        if (action === "grant" || action === "deny") {
          toast.error(
            "Your choice was saved in this browser, but could not be saved to your account."
          );
        } else {
          toast.error("Could not update your preference. Please try again.");
        }
      } finally {
        setIsUpdating(false);
      }
    },
    [posthog, user]
  );

  const accept = useCallback(() => persistAndReload("grant"), [persistAndReload]);
  const decline = useCallback(() => persistAndReload("deny"), [persistAndReload]);
  const revoke = useCallback(() => persistAndReload("revoke"), [persistAndReload]);

  const showBanner = Boolean(
    isPosthogBrowserAnalyticsEnabled &&
    import.meta.env.VITE_PUBLIC_POSTHOG_KEY &&
    isLoaded &&
    consent === "unset"
  );

  return {
    consent,
    showBanner,
    isUpdating,
    accept,
    decline,
    revoke,
  } as const;
}
